import type { AuthError } from "@supabase/supabase-js";

import { registerUser } from "@/api/user/user";

import { paths } from "../routes/paths";
import { supabase } from "../lib/supabase";

import type {
  SignInParams,
  SignUpParams,
  MfaVerifyParams,
  ResetPasswordParams,
  UpdatePasswordParams,
} from "../types/auth";

export const authService = {
  signInWithPassword: async ({ email, password }: SignInParams) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return { data, error };
  },

  // auth-service.ts
  async signInWithGoogle(returnTo?: string) {
    const redirectPath = returnTo || paths.dashboard.root();
    let finalRedirectTo = redirectPath;

    // If we're not already going to the confirmation page, route through it
    if (!finalRedirectTo.includes("/auth/confirm-registration")) {
      finalRedirectTo = `/auth/confirm-registration?returnTo=${encodeURIComponent(
        redirectPath
      )}`;
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}${finalRedirectTo}`,
      },
    });

    if (error) throw error;
    return { data, error };
  },

  signUp: async ({
    email,
    password,
    firstName,
    lastName,
    returnTo,
  }: SignUpParams & { returnTo?: string }) => {
    // Store the original returnTo path as a query parameter for onboarding to use later
    // Ensure we have a string by calling the function if needed
    const dashboardPath = typeof paths.dashboard.root === 'function' 
      ? paths.dashboard.root() 
      : paths.dashboard.root;
    const originalReturnTo = returnTo || dashboardPath;

    // Always redirect to onboarding after email verification
    // Pass the original destination as a query parameter
    const onboardingWithReturnTo = `${
      paths.auth.onboarding
    }?returnTo=${encodeURIComponent(originalReturnTo)}`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}${onboardingWithReturnTo}`,
        data: { display_name: `${firstName} ${lastName}` },
      },
    });

    if (error) throw error;

    if (!data?.user?.identities?.length) {
      throw new Error("This user already exists");
    }

    try {
      const response = await registerUser({
        email,
        id: data.user?.id as string,
        firstName: firstName,
        lastName: lastName,
      });

      if (!response.ok) {
        throw new Error("Failed to create user");
      }
    } catch (error) {
      console.error("Error creating user:", error);
    }

    return { data, error };
  },

  signOut: async (): Promise<{
    error: AuthError | null;
  }> => {
    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error(error);
      throw error;
    }

    return { error };
  },

  resetPassword: async ({ email }: ResetPasswordParams) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${paths.auth.updatePassword}`,
    });

    if (error) throw error;
    return { data, error };
  },

  updatePassword: async ({ password }: UpdatePasswordParams) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
    return { data, error };
  },

  // MFA Methods
  mfaEnroll: async () => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
    });
    if (error) throw error;
    return { data, error };
  },

  mfaGetFactors: async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    return { data, error };
  },

  mfaChallenge: async (factorId: string) => {
    const { data, error } = await supabase.auth.mfa.challenge({
      factorId,
    });
    if (error) throw error;
    return { data, error };
  },

  mfaVerify: async ({ factorId, challengeId, code }: MfaVerifyParams) => {
    const { data, error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code,
    });
    if (error) throw error;
    return { data, error };
  },

  mfaUnenroll: async (factorId: string) => {
    const { data, error } = await supabase.auth.mfa.unenroll({
      factorId,
    });
    if (error) throw error;
    return { data, error };
  },

  isMfaEnabled: async (): Promise<boolean> => {
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      // Check if there are any verified TOTP factors
      return data.totp.some((factor) => factor.status === "verified");
    } catch (error) {
      console.error("Error checking MFA status:", error);
      return false;
    }
  },

  // Update user meta data with recovery options
  updateRecoveryOptions: async (
    recoveryEmail: string,
    recoveryPhone: string
  ) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: {
          recovery_email: recoveryEmail || null,
          recovery_phone: recoveryPhone || null,
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error updating recovery options:", error);
      throw error;
    }
  },

  // Get user recovery options
  getRecoveryOptions: async () => {
    try {
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();

      if (error) throw error;

      return {
        recoveryEmail: user?.user_metadata?.recovery_email || "",
        recoveryPhone: user?.user_metadata?.recovery_phone || "",
      };
    } catch (error) {
      console.error("Error getting recovery options:", error);
      return { recoveryEmail: "", recoveryPhone: "" };
    }
  },
};
